import React, { useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { Alert } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InlineSpinner } from '@/components/ui/inline-spinner';
import { authService } from '../../../services/authService';
import ResourceLinksSection from '../../ResourceLinksSection';
import VolunteerResourcesSection from '../../VolunteerResourcesSection';
import {
  useVolunteerResources,
  useUpdateVolunteerResource,
  VolunteerResourceType,
} from '../../../hooks/queries';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';

const ResourcesTab: React.FC = () => {
  const user = authService.getCurrentUser();
  const isSuperAdmin = user?.isPlatformAdmin ?? false;
  const isAdmin = user?.isPlatformAdmin ?? false;

  // TanStack Query hooks
  const { data: volunteerResources, isLoading, error } = useVolunteerResources();
  const updateResource = useUpdateVolunteerResource();

  // Local UI state for edit dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editingResourceType, setEditingResourceType] = useState<VolunteerResourceType>('quickTips');
  const [editResourceContent, setEditResourceContent] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleEditResource = (resourceType: VolunteerResourceType) => {
    setEditingResourceType(resourceType);
    setEditResourceContent(volunteerResources?.[resourceType] || '');
    setEditDialog(true);
  };

  const handleSaveResource = async () => {
    try {
      await updateResource.mutateAsync({
        resourceType: editingResourceType,
        content: editResourceContent,
      });
      setEditDialog(false);
      setSuccessMessage('Resource updated successfully');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to save resource');
    }
  };

  // Clear messages after 3 seconds
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  if (!user) {
    return (
      <Alert severity="error">
        You must be logged in to view this page.
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className="mb-6">
        {error instanceof Error ? error.message : 'Failed to load volunteer resources'}
      </Alert>
    );
  }

  const displayScript = volunteerResources?.script
    ? volunteerResources.script.replace('[Volunteer Name]', `${user.firstName} ${user.lastName}`)
    : 'No volunteer script available yet.';

  return (
    <>
      <h2 className="mb-2 text-2xl font-semibold">
        Volunteer Resources
      </h2>
      <p className="mb-4 text-sm text-muted-foreground">
        Campaign information, resources, and support for volunteers.
      </p>

      {successMessage && (
        <Alert severity="success" className="mb-4">
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" className="mb-4">
          {errorMessage}
        </Alert>
      )}

      {/* Additional Resource Links - Admins can manage these */}
      <ResourceLinksSection user={user} isAdmin={isAdmin} />

      {/* Use the shared VolunteerResourcesSection component */}
      <VolunteerResourcesSection showQuickTips={false} showQRCode={true} />

      {/* Quick Tips */}
      <Card className="mb-6">
        <CardContent
          className="p-6"
          style={{
            background: 'rgba(47, 28, 106, 0.05)',
            border: '1px solid rgba(47, 28, 106, 0.1)',
          }}
        >
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold" style={{ color: '#2f1c6a' }}>
              Canvassing Quick Tips
            </h3>
            {isSuperAdmin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleEditResource('quickTips')}
                style={{ color: '#2f1c6a' }}
              >
                <Pencil />
              </Button>
            )}
          </div>
          {isLoading ? (
            <InlineSpinner />
          ) : (
            <p
              className="text-sm leading-relaxed whitespace-pre-line"
              style={{ color: '#2f1c6a' }}
            >
              {volunteerResources?.quickTips || 'No quick tips available yet.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Volunteer Script */}
      <Card>
        <CardContent
          className="p-6"
          style={{
            background: 'rgba(47, 28, 106, 0.05)',
            border: '1px solid rgba(47, 28, 106, 0.1)',
          }}
        >
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold" style={{ color: '#2f1c6a' }}>
              Volunteer Script
            </h3>
            {isSuperAdmin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleEditResource('script')}
                style={{ color: '#2f1c6a' }}
              >
                <Pencil />
              </Button>
            )}
          </div>
          {isLoading ? (
            <InlineSpinner />
          ) : (
            <p
              className="text-sm leading-relaxed whitespace-pre-line"
              style={{ color: '#2f1c6a' }}
            >
              {displayScript}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Edit Resource Dialog */}
      <Dialog open={editDialog} onOpenChange={(o) => { if (!o) setEditDialog(false); }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Edit {editingResourceType === 'quickTips' ? 'Quick Tips' : 'Volunteer Script'}
            </DialogTitle>
          </DialogHeader>
          <textarea
            rows={10}
            className="mt-1 flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={editResourceContent}
            onChange={(e) => setEditResourceContent(e.target.value)}
            placeholder={editingResourceType === 'quickTips'
              ? 'Enter quick tips for volunteers...'
              : 'Enter the volunteer script...'
            }
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog(false)} disabled={updateResource.isPending}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveResource}
              disabled={updateResource.isPending}
            >
              {updateResource.isPending && <InlineSpinner />}
              {updateResource.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ResourcesTab;
